import React, { Suspense } from 'react';
import { Navigate, Outlet, useLocation, useRoutes } from 'react-router-dom';

import UnauthenticatedLayout from './layouts/UnauthenticatedLayout';

// non lazy loaded pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import NotFound from './pages/Page404';
import FAQ from './pages/FAQ';
import { Loader } from './components';
import Environments from './pages/company/environments/Environments';
import General from './pages/profile/general';
import { profileTabs } from './pages/profile/constants';
import TabsPageLayout from './components/TabsPageLayout';
import { PORTAL_ROUTE } from './constants/routes';
import Company from './pages/company';
import { companyDetailActionType } from './constants/company';
import EmailInvitation from './pages/EmailInvitation';
import Devices from './pages/devices';
import AppDetails from './pages/apps/appsDetails/AppDetails';
import AppGeneralSection from './pages/apps/appsDetails/AppGeneralSection';
import AppDetailsSection from './pages/apps/appsDetails/AppDetailsSection';
import AppCatalog from './pages/apps/AppCatalog';
import AppPortfolio from './pages/apps/appPortfolio';
import { APP_CATALOG_ROUTE, APP_PORTFOLIO_ROUTE } from './pages/apps/constants';
import { useCanViewAppCatalog, useCanViewAppPortfolio } from './utils/featureFlags';
import SuperAdminGuard from './components/SuperAdminGuard';
import DevicesDashboard from './pages/devices-dashboard';
import DeviceDetails from './pages/devices/deviceDetails/DeviceDetails';
import DeviceGeneralSection from './pages/devices/deviceDetails/general/DeviceGeneralSection';
import DeviceAppResultsSection from './pages/devices/deviceDetails/appResult/DeviceAppResultsSection';
import DeviceLogsSection from './pages/devices/deviceDetails/deviceLogs/DeviceLogsSection';
import UnmatchedFilesSection from './pages/devices/deviceDetails/unmatchedFiles/UnmatchedFiles';
import PackagedAppsSection from './pages/devices/deviceDetails/packagedApps/PackagedAppsSection';
import WDACResults from './pages/wdac-results';
import PackagedApps from './pages/wdac-results/tabs/PackagedApps';
import Win32Apps from './pages/wdac-results/tabs/Win32Apps';
import WDACAppTabs from './pages/wdac-results/tabs/Win32Apps/Tabs';
import WdacAppGeneralTab from './pages/wdac-results/tabs/Win32Apps/Tabs/GeneralTab';
import FilesTab from './pages/wdac-results/tabs/Win32Apps/Tabs/FilesTab';
import TelemetryTab from './pages/wdac-results/tabs/Win32Apps/Tabs/TelemetryTab';
import FileSetsTab from './pages/wdac-results/tabs/Win32Apps/Tabs/FileSetsTab';
import DevicePolicySection from './pages/devices/deviceDetails/policy/PolicySection';
import PolicyDetailsPage from './pages/policyDetails/PolicyDetailsPage';
import CompanyMembers from './pages/company/members';
import CompanyHistory from './pages/company/history';
import Environment from './pages/environment';
import EnvironmentSummary from './pages/environment/tabs/Summary';
import EnvironmentDetectedPolicies from './pages/environment/tabs/DetectedPolicies';
import EnvironmentManagedPolicies from './pages/environment/tabs/ManagedPolicies';
import EnvironmentDeploymentRings from './pages/environment/tabs/DeploymentRings';
import DeploymentRingDetailsPage from './pages/deploymentRingDetails/DeploymentRingDetailsPage';
import DeploymentRingDetailsGeneral from './pages/deploymentRingDetails/Tabs/General';
import DeploymentRingDetailsDevices from './pages/deploymentRingDetails/Tabs/Devices';
import EnvironmentStatusSettings from './pages/environment/tabs/WdacReadinessSettings';
import PolicyDetailsGeneral from './pages/policyDetails/Tabs/General';
import PolicyDevices from './pages/policyDetails/Tabs/Devices';
import PolicyVersions from './pages/policyDetails/Tabs/Versions';
import PackagedAppsTabs from './pages/wdac-results/tabs/PackagedApps/Tabs';
import PackagedDetailsGeneral from './pages/wdac-results/tabs/PackagedApps/Tabs/GeneralTab/PackagedAppDeviceGeneralSection';
import PackagedDetailsImpactedDevices from './pages/wdac-results/tabs/PackagedApps/Tabs/ImpactedDevicesTab';
import ManagedInstallersGeneral from './pages/wdac-results/tabs/WdacManagedInstallers/Tabs/GeneralTab/ManagedInstallersDeviceGeneralSection';
import WdacManagedInstallers from './pages/wdac-results/tabs/WdacManagedInstallers';
import ManagedInstallersTabs from './pages/wdac-results/tabs/WdacManagedInstallers/Tabs';
import ManagedInstallersDevices from './pages/wdac-results/tabs/WdacManagedInstallers/Tabs/DevicesTab';
import ManagedInstallersUnaffectedDevices from './pages/wdac-results/tabs/WdacManagedInstallers/Tabs/UnaffectedDevicesTab';
import ManagedInstallersSection from './pages/devices/deviceDetails/managedInstallers';
import CatalogInventorySection from './pages/devices/deviceDetails/catalogInventory';
import WdacCatalogInventory from './pages/wdac-results/tabs/WdacCatalogInventory';
import CatalogInventoryTabs from './pages/wdac-results/tabs/WdacCatalogInventory/Tabs';
import CatalogInventoryDetails from './pages/wdac-results/tabs/WdacCatalogInventory/Tabs/DetailsTab/CatalogInventoryDetails';
import CatalogInventoryDevices from './pages/wdac-results/tabs/WdacCatalogInventory/Tabs/DevicesTab';
import CatalogInventoryNotConnectedDevices from './pages/wdac-results/tabs/WdacCatalogInventory/Tabs/NotConnectedDevicesTab';
import WdacScriptExecution from './pages/wdac-results/tabs/WdacScriptExecution';
import ScriptExecutionTabs from './pages/wdac-results/tabs/WdacScriptExecution/Tabs';
import ScriptExecutionDetails from './pages/wdac-results/tabs/WdacScriptExecution/Tabs/DetailsTab/ScriptExecutionDetails';
import ScriptExecutionDevices from './pages/wdac-results/tabs/WdacScriptExecution/Tabs/DevicesTab';
import ScriptExecutionSection from './pages/devices/deviceDetails/scriptExecution';
import UnmatchedFiles from './pages/wdac-results/tabs/UnmatchedFiles';

// lazy loaded components for code splitting based on routes
const PortalLayout = React.lazy(() => import('./layouts/portal'));
const Login = React.lazy(() => import('./pages/login/Login'));
const AppsPage = React.lazy(() => import('./pages/apps'));
const ForgotPassword = React.lazy(() => import('./pages/forgotPassword/ForgotPassword'));
const PasswordReset = React.lazy(() => import('./pages/resetPassword/PasswordReset'));
// const Verify = React.lazy(() => import('./pages/Verify'));
const BulkDownloads = React.lazy(() => import('./pages/bulkDownloads/BulkDownloads'));
const CompanyDetails = React.lazy(() => import('./pages/company/companyDetails'));
// ----------------------------------------------------------------------
const Companies = React.lazy(() => import('./pages/companies'));
const AccountExpired = React.lazy(() => import('./pages/accountExpired'));
const SetupAccount = React.lazy(() => import('./pages/setupAccount'));
const MultiFactorSetup = React.lazy(() => import('./pages/mfa/MfaSetup'));

export default function Router() {
  const location = useLocation();
  const canViewAppCatalog = useCanViewAppCatalog();
  const canViewAppPortfolio = useCanViewAppPortfolio();

  // Turning the catalog off hands the section to the portfolio, unless this viewer cannot see the
  // portfolio either -- then the catalog stays reachable rather than leaving them nowhere to land.
  const catalogIsTheOnlyList = canViewAppCatalog || !canViewAppPortfolio;

  return useRoutes([
    {
      path: PORTAL_ROUTE,
      element: (
        <Suspense fallback={<Loader />}>
          <PortalLayout />
        </Suspense>
      ),
      children: [
        {
          path: '',
          element: (
            <Navigate
              to={{
                pathname: '/portal/apps',
                search: location.search,
              }}
            />
          ),
        },
        {
          path: 'apps',
          element: <Outlet />,
          children: [
            {
              path: '',
              element: <AppsPage />,
              children: [
                {
                  index: true,
                  element: (
                    <Navigate to={catalogIsTheOnlyList ? 'app-catalog' : 'app-portfolio'} replace />
                  ),
                },
                {
                  path: 'app-catalog',
                  element: catalogIsTheOnlyList ? (
                    <AppCatalog />
                  ) : (
                    <Navigate to={APP_PORTFOLIO_ROUTE} replace />
                  ),
                },
                {
                  path: 'app-portfolio',
                  element: canViewAppPortfolio ? (
                    <AppPortfolio />
                  ) : (
                    <Navigate to={APP_CATALOG_ROUTE} replace />
                  ),
                },
              ],
            },
            {
              path: ':appId',
              element: <AppDetails />,
              children: [
                {
                  path: 'general',
                  element: <AppGeneralSection />,
                },
                {
                  path: 'details',
                  element: <AppDetailsSection />,
                },
              ],
            },
          ],
        },
        {
          path: 'accounts',
          element: <Outlet />,
          children: [{ path: 'mfa-setup', element: <MultiFactorSetup /> }],
        },
        {
          path: 'profile',
          element: <TabsPageLayout title="Account" tabs={profileTabs} />,
          children: [{ path: 'general', element: <General /> }],
        },
        {
          path: 'company',
          element: <Company />,
          children: [
            {
              path: 'create',
              element: <CompanyDetails actionType={companyDetailActionType.create} />,
            },
            { path: 'details', element: <CompanyDetails /> },
            { path: 'members', element: <CompanyMembers /> },
            { path: 'environments', element: <Environments /> },
            {
              path: 'history',
              element: (
                <SuperAdminGuard redirectTo={`${PORTAL_ROUTE}/company/details`}>
                  <CompanyHistory />
                </SuperAdminGuard>
              ),
            },
          ],
        },
        {
          path: 'account-expired',
          element: <AccountExpired />,
        },
        {
          path: 'bulk-download',
          element: <BulkDownloads />,
        },
        {
          path: 'companies',
          element: <Companies />,
        },
        {
          path: 'devices',
          element: <Outlet />,
          children: [
            { path: '', element: <Devices /> },
            {
              path: ':deviceId',
              element: <DeviceDetails />,
              children: [
                {
                  path: 'general',
                  element: <DeviceGeneralSection />,
                },
                {
                  path: 'win32-apps',
                  element: <DeviceAppResultsSection />,
                },
                {
                  path: 'logs',
                  element: <DeviceLogsSection />,
                },
                {
                  path: 'policy',
                  element: <DevicePolicySection />,
                },
                {
                  path: 'unmatched-files',
                  element: <UnmatchedFilesSection />,
                },
                {
                  path: 'packaged-apps',
                  element: <PackagedAppsSection />,
                },
                {
                  path: 'managed-installers',
                  element: <ManagedInstallersSection />,
                },
                {
                  path: 'catalog-inventory',
                  element: <CatalogInventorySection />,
                },
                {
                  path: 'script-execution',
                  element: <ScriptExecutionSection />,
                },
              ],
            },
          ],
        },
        {
          path: 'deployment-ring-details',
          element: <Outlet />,
          children: [
            {
              path: ':ringId',
              element: <DeploymentRingDetailsPage />,
              children: [
                {
                  path: 'general',
                  element: <DeploymentRingDetailsGeneral />,
                },
                {
                  path: 'devices',
                  element: <DeploymentRingDetailsDevices />,
                },
              ],
            },
          ],
        },
        {
          path: 'policy-details',
          element: <Outlet />,
          children: [
            {
              path: ':policyId',
              element: <PolicyDetailsPage />,
              children: [
                {
                  path: 'general',
                  element: <PolicyDetailsGeneral />,
                },
                {
                  index: true,
                  element: <Navigate to="general" replace />,
                },
                {
                  path: 'versions',
                  element: <PolicyVersions />,
                },
                {
                  // Old links to the tab before it was renamed
                  path: 'prior-versions',
                  element: <Navigate to="../versions" replace />,
                },
                {
                  path: 'devices',
                  element: <PolicyDevices />,
                },
                {
                  // Old links to the tab before devices without the policy joined the devices tab
                  path: 'unaffected-devices',
                  element: <Navigate to="../devices?membership=withoutPolicy" replace />,
                },
              ],
            },
          ],
        },
        {
          path: 'packaged-details',
          children: [
            {
              path: ':packagedAppId',
              element: <PackagedAppsTabs />,
              children: [
                {
                  path: 'general',
                  element: <PackagedDetailsGeneral />,
                },
                {
                  path: 'impacted-devices',
                  element: <PackagedDetailsImpactedDevices />,
                },
              ],
            },
          ],
        },
        {
          path: 'managed-installer-details',
          children: [
            {
              path: ':managedInstallerId',
              element: <ManagedInstallersTabs />,
              children: [
                {
                  path: 'general',
                  element: <ManagedInstallersGeneral />,
                },
                {
                  path: 'devices',
                  element: <ManagedInstallersDevices />,
                },
                {
                  path: 'unaffected-devices',
                  element: <ManagedInstallersUnaffectedDevices />,
                },
              ],
            },
          ],
        },
        {
          path: 'catalog-inventory-details',
          children: [
            {
              path: ':catalogInventoryId',
              element: <CatalogInventoryTabs />,
              children: [
                {
                  path: 'details',
                  element: <CatalogInventoryDetails />,
                },
                {
                  path: 'devices',
                  element: <CatalogInventoryDevices />,
                },
                {
                  path: 'not-connected-devices',
                  element: <CatalogInventoryNotConnectedDevices />,
                },
              ],
            },
          ],
        },
        {
          path: 'script-execution-details',
          children: [
            {
              path: ':scriptExecutionId',
              element: <ScriptExecutionTabs />,
              children: [
                {
                  path: 'details',
                  element: <ScriptExecutionDetails />,
                },
                {
                  path: 'devices',
                  element: <ScriptExecutionDevices />,
                },
              ],
            },
          ],
        },
        {
          path: 'devices-dashboard',
          element: <DevicesDashboard />,
        },
        {
          path: 'wdac-results',
          element: <WDACResults />,
          children: [
            {
              path: 'win32-apps',
              element: <Win32Apps />,
            },
            {
              path: 'packaged-apps',
              element: <PackagedApps />,
            },
            {
              path: 'script-execution',
              element: <WdacScriptExecution />,
            },
            {
              path: 'managed-installers',
              element: <WdacManagedInstallers />,
            },
            {
              path: 'catalog-inventory',
              element: <WdacCatalogInventory />,
            },
            {
              path: 'unmatched-files',
              element: <UnmatchedFiles />,
            },
          ],
        },
        {
          path: 'wdac-apps',
          element: <Outlet />,
          children: [
            { path: '', element: <WDACAppTabs /> },
            {
              path: ':appId',
              element: <WDACAppTabs />,
              children: [
                {
                  index: true,
                  element: <Navigate to="general" replace />,
                },
                {
                  path: 'general',
                  element: <WdacAppGeneralTab />,
                },
                {
                  path: 'files',
                  element: <FilesTab />,
                },
                {
                  path: 'file-sets',
                  element: <FileSetsTab />,
                },
                {
                  path: 'telemetry',
                  element: <TelemetryTab />,
                },
              ],
            },
          ],
        },
        {
          path: 'environment',
          element: <Navigate to={`${PORTAL_ROUTE}/environments/summary`} replace />,
        },
        {
          path: 'environment/summary',
          element: <Navigate to={`${PORTAL_ROUTE}/environments/summary`} replace />,
        },
        {
          path: 'environment/wdac-readiness-settings',
          element: <Navigate to={`${PORTAL_ROUTE}/environments/status-settings`} replace />,
        },
        {
          path: 'environments',
          element: <Environment />,
          children: [
            {
              path: 'summary',
              element: <EnvironmentSummary />,
            },
            {
              path: 'detected-policies',
              element: <EnvironmentDetectedPolicies />,
            },
            {
              path: 'managed-policies',
              element: <EnvironmentManagedPolicies />,
            },
            {
              path: 'deployment-rings',
              element: <EnvironmentDeploymentRings />,
            },
            {
              path: 'status-settings',
              element: <EnvironmentStatusSettings />,
            },
          ],
        },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '/404',
      element: <NotFound />,
    },
    {
      path: '/public',
      element: <Outlet />,
      children: [{ path: 'privacyPolicy', element: <PrivacyPolicy /> }],
    },
    {
      path: '/faq',
      element: <FAQ />,
    },
    {
      path: '/',
      element: <UnauthenticatedLayout />,
      children: [
        {
          path: '/',
          element: (
            <Navigate
              to={{
                pathname: '/portal/apps',
                search: location.search,
              }}
              replace
            />
          ),
        },
        { path: 'login', element: <Login /> },
        // Todo: temp removed. Revert or remove it in the future
        // { path: 'register', element: <Register /> },
        {
          path: 'setup-account',
          element: <SetupAccount />,
        },
        {
          path: 'email-invitation',
          element: <EmailInvitation />,
        },
        { path: 'forgotPassword', element: <ForgotPassword /> },
        { path: 'passwordReset', element: <PasswordReset /> },
        // { path: '/verify', element: <Verify /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" /> },
  ]);
}
